import { useState, useEffect, useRef, useMemo } from 'react';
import { Heart, Feather, BookOpen, Sparkles, Calendar, Pencil } from 'lucide-react';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import type { AnnouncementFormData, RelationshipType } from '@/types/announcement';
import { RELATIONSHIP_OPTIONS } from '@/types/announcement';
import { getSenderPerspectiveLabel } from '@/lib/senderPerspective';

interface LivePreviewCardProps {
  formData: AnnouncementFormData;
  existingPhotoUrls?: string[];
  className?: string;
  compact?: boolean;
}

const TEMPLATE_STYLES: Record<string, {
  bg: string;
  title: string;
  body: string;
  accent: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}> = {
  apology: {
    bg: 'bg-gradient-to-br from-slate-50 via-stone-50 to-zinc-100',
    title: 'text-slate-800',
    body: 'text-slate-600',
    accent: 'text-slate-500',
    label: "I'm Sorry",
    icon: Feather,
  },
  gratitude: {
    bg: 'bg-gradient-to-br from-amber-50 via-orange-50/80 to-yellow-50',
    title: 'text-amber-900',
    body: 'text-amber-800',
    accent: 'text-amber-700',
    label: 'Thank You',
    icon: Heart,
  },
  hard_conversation: {
    bg: 'bg-gradient-to-b from-sky-100 via-blue-50/60 to-rose-50/40',
    title: 'text-slate-800',
    body: 'text-slate-700',
    accent: 'text-sky-600',
    label: 'Something to Share',
    icon: BookOpen,
  },
  love: {
    bg: 'bg-gradient-to-br from-rose-100/90 via-purple-50/70 to-amber-50/80',
    title: 'text-rose-900',
    body: 'text-rose-800',
    accent: 'text-rose-600',
    label: 'With Love',
    icon: Heart,
  },
  closure: {
    bg: 'bg-gradient-to-br from-amber-100 via-yellow-50/90 to-orange-100/80',
    title: 'text-amber-900',
    body: 'text-amber-800',
    accent: 'text-amber-700',
    label: 'Finding Peace',
    icon: Calendar,
  },
  custom: {
    bg: 'bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5',
    title: 'text-foreground',
    body: 'text-muted-foreground',
    accent: 'text-primary',
    label: 'Your Words',
    icon: Sparkles,
  },
};

export function LivePreviewCard({ formData, existingPhotoUrls = [], className, compact = false }: LivePreviewCardProps) {
  const style = TEMPLATE_STYLES[formData.template] || TEMPLATE_STYLES.gratitude;
  const allPhotoUrls = [...existingPhotoUrls, ...formData.photoUrls.slice(existingPhotoUrls.length)];
  const relationship = RELATIONSHIP_OPTIONS.find((r) => r.value === formData.relationship);
  const IconComponent = style.icon;

  // ─── Real-time typing detection ───
  const [isTyping, setIsTyping] = useState(false);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const prevMessageRef = useRef(formData.message);
  const prevNameRef = useRef(formData.recipientName);

  useEffect(() => {
    const messageChanged = formData.message !== prevMessageRef.current;
    const nameChanged = formData.recipientName !== prevNameRef.current;
    prevMessageRef.current = formData.message;
    prevNameRef.current = formData.recipientName;

    if (messageChanged || nameChanged) {
      setIsTyping(true);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => setIsTyping(false), 800);
    }

    return () => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };
  }, [formData.message, formData.recipientName]);

  // ─── Derived display values ───
  const displayMessage = formData.message || 'Your words will appear here as you write…';
  const displayName = formData.recipientName || 'Someone Special';
  const truncatedMessage = compact && displayMessage.length > 200
    ? displayMessage.slice(0, 200) + '…'
    : displayMessage;

  // Split message into lines for animated rendering
  const messageLines = useMemo(() => {
    const lines = truncatedMessage.split('\n').slice(0, compact ? 4 : 8);
    return lines;
  }, [truncatedMessage, compact]);

  // Word count for live stats
  const wordCount = formData.message.trim() ? formData.message.trim().split(/\s+/).length : 0;

  return (
    <motion.div
      className={cn(
        'rounded-2xl overflow-hidden shadow-glass-lg relative isolate',
        compact ? 'min-h-[280px]' : 'min-h-[400px]',
        className
      )}
      layout
      transition={{ duration: 0.3 }}
    >
      {/* Background with smooth template transition */}
      <AnimatePresence mode="wait">
        <motion.div
          key={formData.template}
          className={cn('absolute inset-0', style.bg)}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
        />
      </AnimatePresence>

      {/* Noise texture overlay */}
      <div
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Live sync indicator */}
      <AnimatePresence>
        {isTyping && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="absolute top-3 right-3 z-20 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 backdrop-blur-sm"
          >
            <Pencil className="h-3 w-3 text-primary animate-pulse" />
            <span className="text-[10px] font-medium text-primary">Live</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Content */}
      <div className={cn('relative z-10 flex flex-col items-center text-center', compact ? 'p-5' : 'p-6 md:p-8')}>
        {/* Decorative divider with template transition */}
        <div className="flex items-center gap-2 mb-3">
          <motion.div
            className={cn('w-8 h-px opacity-30', style.accent.replace('text-', 'bg-'))}
            layoutId="divider-left"
          />
          <motion.div
            key={formData.template + '-icon'}
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 0.5 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <IconComponent className={cn('h-3.5 w-3.5', style.accent)} />
          </motion.div>
          <motion.div
            className={cn('w-8 h-px opacity-30', style.accent.replace('text-', 'bg-'))}
            layoutId="divider-right"
          />
        </div>

        {/* Title with template transition */}
        <AnimatePresence mode="wait">
          <motion.h3
            key={style.label}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.3 }}
            className={cn(
              'font-serif tracking-wide mb-1',
              compact ? 'text-lg' : 'text-2xl md:text-3xl',
              style.title
            )}
          >
            {style.label}
          </motion.h3>
        </AnimatePresence>

        {/* Recipient name with live update animation */}
        <motion.p
          key={displayName}
          initial={{ opacity: 0.7 }}
          animate={{ opacity: 1 }}
          className={cn('text-sm mb-4', style.accent)}
        >
          For{' '}
          <motion.span
            key={displayName}
            initial={{ opacity: 0.5, y: 2 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            className="font-medium"
          >
            {displayName}
          </motion.span>
        </motion.p>

        {/* Photos with entry animation */}
        <AnimatePresence>
          {allPhotoUrls.length > 0 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="flex justify-center gap-2 mb-4 flex-wrap"
            >
              {allPhotoUrls.slice(0, 3).map((url, i) => (
                <motion.img
                  key={url}
                  src={url}
                  alt={`Photo ${i + 1}`}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: i * 0.1 }}
                  className={cn(
                    'rounded-lg object-cover shadow-md border-2 border-white/70',
                    compact ? 'w-14 h-14' : 'w-20 h-20 md:w-24 md:h-24'
                  )}
                />
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Message preview with line-by-line animation */}
        <div className={cn('max-w-sm mx-auto mb-4 w-full', compact ? 'space-y-1' : 'space-y-2')}>
          {messageLines.map((line, i) => (
            <motion.p
              key={`${i}-${line.slice(0, 20)}`}
              initial={false}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.15 }}
              className={cn(
                'leading-relaxed',
                compact ? 'text-xs' : 'text-sm md:text-base',
                style.body,
                line.trim() === '' ? 'h-2' : '',
                !formData.message && 'italic opacity-50'
              )}
            >
              {line || '\u00A0'}
            </motion.p>
          ))}
        </div>

        {/* Signature with smooth appearance */}
        <AnimatePresence>
          {relationship && (
            <motion.p
              key={relationship.value}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              className={cn('text-xs italic', style.accent)}
            >
              — From your {getSenderPerspectiveLabel(relationship.value)}
            </motion.p>
          )}
        </AnimatePresence>

        {/* Live word count */}
        {formData.message && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center gap-3 mt-4"
          >
            <span className={cn('text-[9px] tracking-wider uppercase opacity-40', style.body)}>
              {wordCount} {wordCount === 1 ? 'word' : 'words'}
            </span>
          </motion.div>
        )}

        {/* Branding */}
        <p className={cn('text-[8px] opacity-30 mt-3 tracking-wider', style.body)}>
          Say It NOW • While They're Still Here
        </p>
      </div>
    </motion.div>
  );
}
